import type {
    SiteBuilderLeadForm,
    SiteBuilderLeadFormField,
} from "./siteBuilderTypes";

export type SiteBuilderLeadValue = string | string[];
export type SiteBuilderLeadValues = Record<string, SiteBuilderLeadValue>;

export type SiteBuilderLeadSubmission = {
    endpoint: string;
    fields: SiteBuilderLeadValues;
    form: SiteBuilderLeadForm | null;
    honeypotValue?: string;
    landingUrl?: string;
    purpose: string;
    startedAt: number;
};

export type SiteBuilderLeadSubmissionResult = {
    message: string | null;
    ok: boolean;
};

/** Mirrors the backend builder-form key normalizer for authored field blocks. */
export function siteBuilderLeadFieldKey(
    value: string,
    position: number,
): string {
    const trimmed = value.trim();
    const snakeCase = `${trimmed.slice(0, 1)}${trimmed.slice(1).replace(/[A-Z]/gu, "_$&")}`;
    const normalized = snakeCase
        .toLowerCase()
        .replace(/[^a-z0-9]+/gu, "_")
        .replace(/^_+|_+$/gu, "");
    const fallbackPosition = Math.max(0, Math.trunc(position)) + 1;

    return (normalized || `field_${fallbackPosition}`).slice(0, 64);
}

type LeadSubmitResponse = {
    data?: {
        message?: unknown;
    };
    message?: unknown;
};

function record(value: unknown): Record<string, unknown> {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value as Record<string, unknown>
        : {};
}

function nonEmptyString(value: unknown): string | null {
    if (typeof value !== "string") {
        return null;
    }

    const normalized = value.trim();

    return normalized === "" ? null : normalized;
}

function formFieldFromValue(value: unknown): SiteBuilderLeadFormField | null {
    const source = record(value);
    const key = nonEmptyString(source.key);
    const label = nonEmptyString(source.label);
    const type = nonEmptyString(source.type);

    if (!key || !label || !type) {
        return null;
    }

    return {
        uuid: nonEmptyString(source.uuid) ?? undefined,
        key,
        label,
        type,
        required: source.required === true,
        placeholder: nonEmptyString(source.placeholder),
        options: Array.isArray(source.options)
            ? source.options.filter((option): option is string => (
                typeof option === "string" && option.trim() !== ""
            ))
            : [],
    };
}

/** Parses the public form response once for Web, Morrow, Laguna and future skins. */
export function siteBuilderLeadFormFromValue(
    value: unknown,
    expectedPurpose?: string | null,
): SiteBuilderLeadForm | null {
    const source = record(value);
    const uuid = nonEmptyString(source.uuid);
    const name = nonEmptyString(source.name);
    const purpose = nonEmptyString(source.purpose);
    const normalizedExpectedPurpose = expectedPurpose?.trim() ?? "";

    if (
        !uuid
        || !name
        || !purpose
        || !Array.isArray(source.fields)
        || (normalizedExpectedPurpose !== "" && purpose !== normalizedExpectedPurpose)
    ) {
        return null;
    }

    const fields = source.fields
        .map((item, index) => ({
            field: formFieldFromValue(item),
            position: Number(record(item).position ?? index),
        }))
        .filter((item): item is { field: SiteBuilderLeadFormField; position: number } => (
            item.field !== null
        ))
        .sort((left, right) => (
            (Number.isFinite(left.position) ? left.position : 0)
            - (Number.isFinite(right.position) ? right.position : 0)
        ))
        .map((item) => item.field);

    return { fields, name, purpose, uuid };
}

export function siteBuilderLeadValuesFromFormData(
    formData: FormData,
): SiteBuilderLeadValues {
    const values: SiteBuilderLeadValues = {};

    formData.forEach((rawValue, key) => {
        const value = typeof rawValue === "string" ? rawValue : rawValue.name;
        const current = values[key];

        if (current === undefined) {
            values[key] = value;
        } else if (Array.isArray(current)) {
            current.push(value);
        } else {
            values[key] = [current, value];
        }
    });

    return values;
}

export function siteBuilderLeadSubmissionPayload({
    fields,
    form,
    honeypotValue = "",
    landingUrl = "",
    purpose,
    startedAt,
}: Omit<SiteBuilderLeadSubmission, "endpoint">): Record<string, SiteBuilderLeadValue | number | undefined> {
    return {
        ...fields,
        formUuid: form?.uuid,
        honeypotValue,
        landingUrl,
        purpose: form?.purpose ?? purpose,
        startedAt,
    };
}

async function submissionMessage(response: Response): Promise<string | null> {
    try {
        const payload = record(await response.json()) as LeadSubmitResponse;
        const nestedMessage = nonEmptyString(payload.data?.message);

        return nestedMessage ?? nonEmptyString(payload.message);
    } catch {
        return null;
    }
}

export async function submitSiteBuilderLead(
    submission: SiteBuilderLeadSubmission,
): Promise<SiteBuilderLeadSubmissionResult> {
    const response = await fetch(submission.endpoint, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(siteBuilderLeadSubmissionPayload(submission)),
    });

    return {
        message: await submissionMessage(response),
        ok: response.ok,
    };
}
